import { Network } from '../types';

// Variables de entorno para OMDB
const OMDB_RPC_URL = import.meta.env.VITE_OMDB_RPC_URL || '/api/proxy/omdb-rpc';
const OMDB_EXPLORER_URL = import.meta.env.VITE_OMDB_EXPLORER_URL || 'https://omdbscan.com';
export const OMDB_CHAIN_ID = parseInt(import.meta.env.VITE_OMDB_CHAIN_ID) || 9580;

// Dirección del contrato del token OMD en BSC
const OMD_TOKEN_ADDRESS = import.meta.env.VITE_OMD_TOKEN_ADDRESS || '0xA7670e2e6742a18029436E262b01F7C50A863C40';

// Alchemy RPC URLs — usa los mismos keys que el sistema de transacciones
const ALCHEMY_ETH = `https://eth-mainnet.g.alchemy.com/v2/${import.meta.env.VITE_ALCHEMY_KEY_ETHEREUM}`;
const ALCHEMY_POLY = `https://polygon-mainnet.g.alchemy.com/v2/${import.meta.env.VITE_ALCHEMY_KEY_POLYGON}`;
const ALCHEMY_ARB = `https://arb-mainnet.g.alchemy.com/v2/${import.meta.env.VITE_ALCHEMY_KEY_ARBITRUM}`;
const ALCHEMY_OPT = `https://opt-mainnet.g.alchemy.com/v2/${import.meta.env.VITE_ALCHEMY_KEY_OPTIMISM}`;
const ALCHEMY_BSC = `https://bnb-mainnet.g.alchemy.com/v2/${import.meta.env.VITE_ALCHEMY_KEY_BSC}`;

/**
 * Definición de todas las redes blockchain soportadas
 * 
 * REDES SOPORTADAS (6 redes):
 * - OMDB: Explorer propio
 * - Ethereum: Alchemy
 * - Polygon: Alchemy
 * - Arbitrum: Alchemy
 * - Optimism: Alchemy
 * - BSC: BSCScan API
 */
export const networks: Network[] = [
  {
    id: 'omdb',
    name: 'OMDBlockchain',
    nativeCurrency: {
      name: 'OMDB',
      symbol: 'OMDB',
      decimals: 18,
    },
    chainId: OMDB_CHAIN_ID,
    rpcUrl: OMDB_RPC_URL,
    wsUrl: OMDB_RPC_URL.replace('https://', 'wss://').replace('http://', 'wss://') + '/ws',
    blockExplorerUrl: OMDB_EXPLORER_URL,
    logoUrl: '/assets/omdb-local-logo.png',
    nativeCurrencyLogoUrl: '/assets/omdb-local-logo.png',
    isTestnet: false,
    tokens: [
      {
        address: '0x0000000000000000000000000000000000000000',
        name: 'OMDB',
        symbol: 'OMDB',
        decimals: 18,
        isNative: true,
        logoUrl: '/assets/omdb-local-logo.png',
      },
    ],
  },
  {
    id: 'bsc',
    name: 'Binance Smart Chain',
    nativeCurrency: {
      name: 'Binance Coin',
      symbol: 'BNB',
      decimals: 18,
    },
    chainId: 56,
    rpcUrl: ALCHEMY_BSC,
    wsUrl: 'wss://bsc-ws-node.nariox.org:443',
    blockExplorerUrl: 'https://bscscan.com',
    logoUrl: '/assets/bsc-logo.png',
    nativeCurrencyLogoUrl: '/assets/bsc-logo.png',
    isTestnet: false,
    tokens: [
      {
        address: OMD_TOKEN_ADDRESS,
        name: 'OneMillionDollars',
        symbol: 'OMD',
        decimals: 8,
        isNative: false,
        isPrimary: true,
        logoUrl: '/assets/omdb-local-logo.png',
      },
    ],
  },
  {
    id: 'ethereum',
    name: 'Ethereum',
    nativeCurrency: {
      name: 'Ethereum',
      symbol: 'ETH',
      decimals: 18,
    },
    chainId: 1,
    rpcUrl: ALCHEMY_ETH,
    wsUrl: 'wss://eth-mainnet.g.alchemy.com/v2/' + import.meta.env.VITE_ALCHEMY_KEY_ETHEREUM,
    blockExplorerUrl: 'https://etherscan.io',
    logoUrl: '/assets/ethereum-logo.png',
    nativeCurrencyLogoUrl: '/assets/ethereum-logo.png',
    isTestnet: false,
  },
  {
    id: 'polygon',
    name: 'Polygon',
    nativeCurrency: {
      name: 'Polygon',
      symbol: 'MATIC',
      decimals: 18,
    },
    chainId: 137,
    rpcUrl: ALCHEMY_POLY,
    wsUrl: 'wss://polygon-mainnet.g.alchemy.com/v2/' + import.meta.env.VITE_ALCHEMY_KEY_POLYGON,
    blockExplorerUrl: 'https://polygonscan.com',
    logoUrl: '/assets/polygon-logo.png',
    nativeCurrencyLogoUrl: '/assets/polygon-logo.png',
    isTestnet: false,
  },
  {
    id: 'arbitrum',
    name: 'Arbitrum',
    nativeCurrency: {
      name: 'Ethereum',
      symbol: 'ETH',
      decimals: 18,
    },
    chainId: 42161,
    rpcUrl: ALCHEMY_ARB,
    wsUrl: 'wss://arb-mainnet.g.alchemy.com/v2/' + import.meta.env.VITE_ALCHEMY_KEY_ARBITRUM,
    blockExplorerUrl: 'https://arbiscan.io',
    logoUrl: '/assets/arbitrum-logo.png',
    // Arbitrum usa ETH como gas, por eso el logo del token nativo apunta a Ethereum
    nativeCurrencyLogoUrl: '/assets/ethereum-logo.png',
    isTestnet: false,
  },
  {
    id: 'optimism',
    name: 'Optimism',
    nativeCurrency: {
      name: 'Ethereum',
      symbol: 'ETH',
      decimals: 18,
    },
    chainId: 10,
    rpcUrl: ALCHEMY_OPT,
    wsUrl: 'wss://opt-mainnet.g.alchemy.com/v2/' + import.meta.env.VITE_ALCHEMY_KEY_OPTIMISM,
    blockExplorerUrl: 'https://optimistic.etherscan.io',
    logoUrl: '/assets/optimism-logo.png',
    // Optimism usa ETH como gas, por eso el logo del token nativo apunta a Ethereum
    nativeCurrencyLogoUrl: '/assets/ethereum-logo.png',
    isTestnet: false,
  },
  {
    id: 'glak',
    name: 'GLAK Testnet',
    nativeCurrency: {
      name: 'GLAK',
      symbol: 'GLAK',
      decimals: 18,
    },
    chainId: 35039980,
    rpcUrl: 'http://158.69.203.100:8545',
    wsUrl: 'ws://158.69.203.100:8545',
    isTestnet: true,
  },
];

// Redes agregadas por el usuario. Van en su propia clave de localStorage (no en
// storage.service.ts) para evitar un import circular con config/index.ts.
const CUSTOM_NETWORKS_KEY = 'custom_networks';

export const getCustomNetworks = (): Network[] => {
  try {
    const raw = localStorage.getItem(CUSTOM_NETWORKS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error('[Networks] Error leyendo redes personalizadas:', error);
    return [];
  }
};

export const getAllNetworks = (): Network[] => {
  return [...networks, ...getCustomNetworks()];
};

/** Rechaza duplicados por chainId, contra redes propias o ya agregadas */
export const saveCustomNetwork = (
  network: Network
): { success: boolean; error?: 'chain_id_exists' | 'storage_error' } => {
  const alreadyExists = getAllNetworks().some((n) => n.chainId === network.chainId);
  if (alreadyExists) {
    return { success: false, error: 'chain_id_exists' };
  }

  try {
    const custom = getCustomNetworks();
    custom.push(network);
    localStorage.setItem(CUSTOM_NETWORKS_KEY, JSON.stringify(custom));
    return { success: true };
  } catch (error) {
    console.error('[Networks] Error guardando red personalizada:', error);
    return { success: false, error: 'storage_error' };
  }
};

/**
 * Actualiza una red personalizada existente por id. El id nunca cambia.
 * Si el chainId cambia, se valida contra duplicados excluyendo la propia red.
 */
export const updateCustomNetwork = (
  id: string,
  updates: Pick<
    Network,
    'name' | 'rpcName' | 'rpcUrl' | 'chainId' | 'blockExplorerUrl' | 'nativeCurrency'
  >
): { success: boolean; error?: 'chain_id_exists' | 'not_found' | 'storage_error' } => {
  const custom = getCustomNetworks();
  const index = custom.findIndex((n) => n.id === id);
  if (index === -1) {
    return { success: false, error: 'not_found' };
  }

  const collision = getAllNetworks().some(
    (n) => n.id !== id && n.chainId === updates.chainId
  );
  if (collision) {
    return { success: false, error: 'chain_id_exists' };
  }

  try {
    custom[index] = { ...custom[index], ...updates };
    localStorage.setItem(CUSTOM_NETWORKS_KEY, JSON.stringify(custom));
    return { success: true };
  } catch (error) {
    console.error('[Networks] Error actualizando red personalizada:', error);
    return { success: false, error: 'storage_error' };
  }
};

export const removeCustomNetwork = (id: string): boolean => {
  const custom = getCustomNetworks();
  const filtered = custom.filter((n) => n.id !== id);
  if (filtered.length === custom.length) return false;

  localStorage.setItem(CUSTOM_NETWORKS_KEY, JSON.stringify(filtered));
  return true;
};

/**
 * Obtiene una red por su ID
 */
export const getNetwork = (networkId: string): Network => {
  return getAllNetworks().find((network) => network.id === networkId) || networks[0];
};

/**
 * Obtiene una red por su chainId
 */
export const getNetworkByChainId = (chainId: number): Network | null => {
  return getAllNetworks().find((network) => network.chainId === chainId) || null;
};

/**
 * URL al detalle de una tx en el explorador; null si la red no tiene uno.
 */
export const getTxExplorerUrl = (networkId: string, hash: string): string | null => {
  const url = getNetwork(networkId)?.blockExplorerUrl;
  return url ? `${url.replace(/\/$/, '')}/tx/${hash}` : null;
};

/**
 * Obtiene la URL del logo de una red
 */
export const getNetworkLogoUrl = (chainId: number): string => {
  const trustWalletMapping: Record<number, string> = {
    1: 'ethereum',
    56: 'smartchain',
    137: 'polygon',
    42161: 'arbitrum',
    10: 'optimism',
    [OMDB_CHAIN_ID]: 'omdb',
  };

  const trustWalletId = trustWalletMapping[chainId];

  if (trustWalletId === 'omdb') {
    return '/assets/omdb-local-logo.png';
  }

  if (trustWalletId) {
    return `https://assets.trustwalletapp.com/blockchains/${trustWalletId}/info/logo.png`;
  }

  return 'https://assets.trustwalletapp.com/blockchains/ethereum/info/logo.png';
};

/**
 * Obtiene la URL del logo de un token
 */
export const getTokenLogoUrl = (address: string, chainId: number): string | null => {
  if (!address) return null;

  let normalizedAddress = address.toLowerCase();
  if (normalizedAddress.startsWith('0x')) {
    normalizedAddress = normalizedAddress.substring(2);
  }

  const trustWalletChainMapping: Record<number, string> = {
    1: 'ethereum',
    56: 'smartchain',
    137: 'polygon',
    42161: 'arbitrum',
    10: 'optimism',
    [OMDB_CHAIN_ID]: 'omdb',
  };

  const trustWalletChain = trustWalletChainMapping[chainId] || 'ethereum';

  if (trustWalletChain === 'omdb') {
    return '/assets/omdb-local-logo.png';
  }

  return `https://assets.trustwalletapp.com/blockchains/${trustWalletChain}/assets/${normalizedAddress}/logo.png`;
};
